import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { AvatarModule } from 'ngx-avatars';
import moment from 'moment';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectService } from 'src/app/Services/project.service';
import { Router } from '@angular/router';
import { ConductTimelogService } from 'src/app/Services/conduct-timelog.service';
import { DummyDataService } from 'src/app/Services/dummy-data.service';
import { SharedService } from 'src/app/Services/sharedService';
import { ConductTimelog, ConductTimelogGroup } from 'src/app/interface/conduct-timelog';
import { Employee } from 'src/app/interface/employee';
import { colors } from 'src/app/colors';
import { processTimelineSessionLogs, TimelineRenderedSession } from 'src/app/utils/timeline-session-logs.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ConductTimelogsSummaryCardComponent } from './conduct-timelogs-summary-card/conduct-timelogs-summary-card.component';

export interface TimelineSegment {
  leftPercent: number;
  widthPercent: number;
  label: string;
  tooltip: string;
  hexColor: string;
  isIdle?: boolean;
}

export interface TaskTimelineSegment extends TimelineSegment {
  log: ConductTimelog;
  taskTitle: string;
  taskId?: string;
  projectName: string;
  startMin: number;
  endMin: number;
  timeRangeText: string;
  durationText: string;
  activeOverlapText: string;
  differenceText: string;
  differenceSecs: number;
  superSeeTimeRangeText: string;
  superSeeDurationText: string;
  activeSecs: number;
  idleSecs: number;
  offlineSecs?: number;
  idleText?: string;
  offlineText?: string;
}

export interface UnloggedTimelineSegment {
  leftPercent: number;
  widthPercent: number;
  startMin: number;
  endMin: number;
  durationSecs: number;
  timeRangeLabel: string;
  durationLabel: string;
  tooltip: string;
}

export interface ConductTaskFeedItem {
  type: 'task';
  startMin: number;
  endMin: number;
  log: ConductTimelog;
  projectName: string;
  taskUniqueId?: string;
  taskTitle: string;
  timeRange: string;
  durationBadge: string;
  projectPalette?: ProjectColorPalette;
}

export interface ConductUnloggedFeedItem {
  type: 'unlogged';
  startMin: number;
  endMin: number;
  durationSecs: number;
  timeRange: string;
  durationLabel: string;
  tooltip: string;
}

export type ConductTimelineFeedItem = ConductTaskFeedItem | ConductUnloggedFeedItem;

export interface ProjectColorPalette {
  tagBg: string;
  tagText: string;
  tagBorder: string;
  barHex: string;
}

export interface DrawerDayCard {
  date: Date;
  dateStr: string;
  dayOfWeek: string;
  dayOfMonth: string;
  fullLabel: string;
  isSelected: boolean;
  dotColorClass: string;
  dotTooltip: string;
}

const PROJECT_PALETTES: ProjectColorPalette[] = [
  {
    tagBg: 'bg-indigo-50',
    tagText: 'text-indigo-700',
    tagBorder: 'border-indigo-100',
    barHex: '#5459d1'
  },
  {
    tagBg: 'bg-amber-50',
    tagText: 'text-amber-800',
    tagBorder: 'border-amber-200',
    barHex: '#f59e0b'
  },
  {
    tagBg: 'bg-pink-50',
    tagText: 'text-pink-700',
    tagBorder: 'border-pink-200',
    barHex: '#ec4899'
  },
  {
    tagBg: 'bg-emerald-50',
    tagText: 'text-emerald-700',
    tagBorder: 'border-emerald-200',
    barHex: '#10b981'
  },
  {
    tagBg: 'bg-sky-50',
    tagText: 'text-sky-700',
    tagBorder: 'border-sky-200',
    barHex: '#0284c7'
  },
  {
    tagBg: 'bg-purple-50',
    tagText: 'text-purple-700',
    tagBorder: 'border-purple-200',
    barHex: '#8b5cf6'
  },
  {
    tagBg: 'bg-teal-50',
    tagText: 'text-teal-700',
    tagBorder: 'border-teal-200',
    barHex: '#0d9488'
  }
];

@Component({
  selector: 'app-conduct-timelogs-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AvatarModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgxSkeletonLoaderModule,
    ConductTimelogsSummaryCardComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  templateUrl: './conduct-timelogs-drawer.component.html',
  styleUrls: ['./conduct-timelogs-drawer.component.css']
})
export class ConductTimelogsDrawerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() group: ConductTimelogGroup | null = null;
  @Input() groups: ConductTimelogGroup[] = [];
  @Input() currentIndex = 0;
  @Input() selectedDate: string | Date | moment.Moment = new Date();
  @Input() startDate?: string | Date | moment.Moment;
  @Input() endDate?: string | Date | moment.Moment;
  @Input() users: Employee[] = [];
  @Input() userTimelineSessions: any[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() navigateIndex = new EventEmitter<number>();

  desktopTimeFormatted = '00:00';
  desktopTimeSubtitle = 'No timeline recorded';
  hasTimelineData = false;
  conductTimeFormatted = '0h 00m';
  timeVarianceFormatted = '0m';
  matchPercentage = 0;
  unloggedTimeFormatted = '0h 00m';
  unloggedGapsCount = 0;
  timelineRangeLabel = '';
  formattedDateSubtitle = '';
  userRoleSubtitle = 'Team Member';

  userTimelineSegments: TimelineSegment[] = [];
  systemUsageSegments: TimelineSegment[] = [];
  taskLogSegments: TaskTimelineSegment[] = [];
  unloggedSegments: UnloggedTimelineSegment[] = [];
  timelineTicks: { label: string; leftPercent: number }[] = [];
  /** sortedLogs is enriched with a stable resolvedColor so template bindings never call methods. */
  sortedLogs: (ConductTimelog & { resolvedColor: string })[] = [];
  interleavedFeed: ConductTimelineFeedItem[] = [];
  isLoadingDesktopTime = false;

  activeTaskChip: TaskTimelineSegment | null = null;
  taskTooltipX = 0;
  taskTooltipY = 0;
  isTooltipPinned = false;

  get timelineDesktopHours(): string {
    return this.cleanHourFormat(this.desktopTimeFormatted);
  }

  get timelineConductHours(): string {
    return this.cleanHourFormat(this.conductTimeFormatted);
  }

  get timelineUnloggedHours(): string {
    return this.cleanHourFormat(this.unloggedTimeFormatted);
  }

  onTaskChipClick(seg: TaskTimelineSegment, event: MouseEvent): void {
    event.stopPropagation();
    if (this.activeTaskChip === seg && this.isTooltipPinned) {
      this.closeTaskTooltip();
      return;
    }
    this.isTooltipPinned = true;
    this.activeTaskChip = seg;
    this.updateTooltipPosition(event);

    const taskId = seg.log?.task_unique_id || seg.log?.task_id;
    if (taskId) {
      const el = document.getElementById(`task-row-${taskId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  onTaskChipMouseEnter(seg: TaskTimelineSegment, event: MouseEvent): void {
    if (this.isTooltipPinned) return;
    this.activeTaskChip = seg;
    this.updateTooltipPosition(event);
  }

  onTaskChipMouseLeave(): void {
    if (this.isTooltipPinned) return;
    this.activeTaskChip = null;
  }

  closeTaskTooltip(): void {
    this.activeTaskChip = null;
    this.isTooltipPinned = false;
  }

  onTableRowClick(item: ConductTimelineFeedItem): void {
    if (item.type !== 'task') return;
    const matchedSeg = this.taskLogSegments.find((s) => s.log === item.log);
    if (matchedSeg) {
      this.activeTaskChip = matchedSeg;
      this.isTooltipPinned = true;
      const el = document.getElementById(`task-chip-${matchedSeg.taskId || matchedSeg.startMin}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        this.taskTooltipX = Math.round(rect.left + rect.width / 2 - 165);
        this.taskTooltipY = Math.round(rect.bottom + 10);
      }
    }
  }

  navigateToWorkReplay(log?: ConductTimelog | null): void {
    const targetLog = log || this.activeTaskChip?.log;
    if (!targetLog) return;
    const employeeId = targetLog.supersee_user_id ?? targetLog.supersee_user ?? this.group?.supersee_user_id;
    const dateStr = this.getTargetDateStr();
    const queryParams: Record<string, string> = {};
    if (employeeId) {
      queryParams['employeeId'] = String(employeeId);
    }
    if (dateStr) {
      queryParams['date'] = dateStr;
    }
    queryParams['showConduct'] = 'true';
    if (targetLog.project_name && String(targetLog.project_name).trim()) {
      queryParams['project'] = String(targetLog.project_name).trim().toLowerCase();
    }
    const rawTime = targetLog.from_time || (targetLog as any).start_time;
    if (rawTime && String(rawTime).trim()) {
      queryParams['time'] = String(rawTime).trim();
    }
    this.closeTaskTooltip();
    this.closeDrawer();
    void this.router.navigate(['/work-replay'], { queryParams });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.activeTaskChip) {
      const target = event.target as HTMLElement;
      if (!target.closest('.conduct-task-white-tooltip') && !target.closest('.task-chip-pill')) {
        this.closeTaskTooltip();
      }
    }
  }

  private updateTooltipPosition(event: MouseEvent): void {
    const tooltipWidth = 330;
    const tooltipHeight = 260;
    const padding = 16;
    let x = event.clientX - (tooltipWidth / 2);
    let y = event.clientY + 15;

    if (x + tooltipWidth > window.innerWidth - padding) {
      x = window.innerWidth - tooltipWidth - padding;
    }
    if (x < padding) {
      x = padding;
    }

    if (y + tooltipHeight > window.innerHeight - padding) {
      y = event.clientY - tooltipHeight - 15;
    }
    if (y < padding) {
      y = padding;
    }

    this.taskTooltipX = Math.round(x);
    this.taskTooltipY = Math.round(y);
  }

  cleanHourFormat(val: string): string {
    if (!val || val === '00:00' || val === '00h 00m') {
      return '0h 00m';
    }
    return val.replace(/^0(\d)h/, '$1h');
  }

  drawerSelectedDate: Date = new Date();
  visibleDayCards: DrawerDayCard[] = [];

  get effectiveStartDate(): Date {
    const d = this.startDate || this.selectedDate;
    return moment(d).startOf('day').toDate();
  }

  get effectiveEndDate(): Date {
    const d = this.endDate || this.startDate || this.selectedDate;
    return moment(d).endOf('day').toDate();
  }

  get isMultipleDates(): boolean {
    return !moment(this.effectiveStartDate).isSame(moment(this.effectiveEndDate), 'day');
  }

  cardWindowStartIndex = 0;

  get visibleCardWindowSize(): number {
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return 5;
    }
    return 7;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen && this.isMultipleDates) {
      this.rebuildVisibleDayCards();
    }
  }

  get formattedDrawerSelectedDateFull(): string {
    return moment(this.drawerSelectedDate).format('ddd, DD MMM YYYY');
  }

  get allDaysInRange(): Date[] {
    const days: Date[] = [];
    const start = moment(this.effectiveStartDate).startOf('day');
    const end = moment(this.effectiveEndDate).startOf('day');
    const curr = start.clone();
    while (curr.isSameOrBefore(end, 'day')) {
      days.push(curr.toDate());
      curr.add(1, 'day');
    }
    return days;
  }

  onPrevDayCard(): void {
    if (this.isDrawerPrevDateDisabled()) {
      return;
    }
    this.onDrawerPrevDate();
  }

  onNextDayCard(): void {
    if (this.isDrawerNextDateDisabled()) {
      return;
    }
    this.onDrawerNextDate();
  }

  getDayMatchHealth(dateStr: string): { colorClass: string; tooltip: string } {
    if (!this.group || !Array.isArray(this.group.logs)) {
      return { colorClass: 'bg-slate-300', tooltip: 'No activity recorded' };
    }

    const dayLogs = this.group.logs.filter(l => this.extractLogDate(l) === dateStr);
    const dayConductSecs = dayLogs.reduce((acc, l) => acc + this.getLogDurationSeconds(l), 0);

    const userId = this.group?.supersee_user_id || this.group?.user_key;
    const cacheKey = `${userId}_${dateStr}`;
    const cachedSessions = this.timelineCache.get(cacheKey);

    let desktopSecs = 0;
    if (Array.isArray(cachedSessions) && cachedSessions.length > 0) {
      cachedSessions.forEach((s: any) => {
        if (!this.sessionMatchesDate(s, dateStr)) return;
        const inTime = s.inTime ?? s.in;
        const outTime = s.outTime ?? s.out;
        if (inTime && outTime) {
          const sMin = this.parseTimeToMinutes(inTime);
          const eMin = this.parseTimeToMinutes(outTime);
          if (eMin > sMin) {
            desktopSecs += (eMin - sMin) * 60;
          }
        }
      });
    }

    if (dayConductSecs === 0 && desktopSecs === 0) {
      return { colorClass: 'bg-slate-300', tooltip: 'No activity recorded' };
    }

    if (desktopSecs > 0) {
      const matchRate = Math.min(
        100,
        Math.round((Math.min(dayConductSecs, desktopSecs) / Math.max(dayConductSecs, desktopSecs)) * 100)
      );
      if (matchRate >= 80) {
        return { colorClass: 'bg-emerald-500', tooltip: `High match (${matchRate}%)` };
      }
      if (matchRate >= 50) {
        return { colorClass: 'bg-amber-500', tooltip: `Moderate gap (${matchRate}%)` };
      }
      return { colorClass: 'bg-rose-500', tooltip: `Low match (${matchRate}%)` };
    }

    if (dayConductSecs > 0) {
      return { colorClass: 'bg-emerald-500', tooltip: `${this.formatDurationHhMm(dayConductSecs)} logged` };
    }

    return { colorClass: 'bg-slate-300', tooltip: 'No activity recorded' };
  }

  get formattedDrawerSelectedDate(): string {
    return moment(this.drawerSelectedDate).format('DD MMM YYYY');
  }

  get isNoActivity(): boolean {
    return (!this.hasTimelineData || this.desktopTimeFormatted === '00:00' || this.desktopTimeFormatted === '0h 00m') &&
      (!this.group || !this.group.logs || this.group.logs.length === 0 || this.conductTimeFormatted === '0h 00m' || this.conductTimeFormatted === '00h 00m');
  }

  get isHealthyAlignment(): boolean {
    return !this.isNoActivity && this.matchPercentage >= 80;
  }

  get isModerateDiscrepancy(): boolean {
    return !this.isNoActivity && this.matchPercentage >= 50 && this.matchPercentage < 80;
  }

  get isHighDiscrepancy(): boolean {
    return !this.isNoActivity && this.matchPercentage < 50;
  }

  get matchBadgeClass(): string {
    if (this.isNoActivity) {
      return 'bg-slate-100 text-slate-600 border-slate-200';
    }
    if (this.isHealthyAlignment) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (this.isModerateDiscrepancy) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }

  get matchBadgeIcon(): string {
    if (this.isNoActivity) {
      return 'schedule';
    }
    if (this.isHealthyAlignment) {
      return 'check';
    }
    if (this.isModerateDiscrepancy) {
      return 'priority_high';
    }
    return 'warning';
  }

  get varianceTextColorClass(): string {
    if (this.isHealthyAlignment) {
      return 'text-emerald-600';
    }
    if (this.isModerateDiscrepancy) {
      return 'text-amber-600';
    }
    return 'text-rose-600';
  }

  get varianceIconContainerClass(): string {
    if (this.isHealthyAlignment) {
      return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    }
    if (this.isModerateDiscrepancy) {
      return 'bg-amber-50 text-amber-600 border-amber-100';
    }
    return 'bg-rose-50 text-rose-600 border-rose-100';
  }

  get varianceIcon(): string {
    if (this.isHealthyAlignment) {
      return 'check_circle';
    }
    if (this.isModerateDiscrepancy) {
      return 'timelapse';
    }
    return 'compare_arrows';
  }

  private static readonly EXCLUDED_CONDUCT_THEME_COLORS = new Set([
    '#2143bc',
    '#6521bc',
    '#a621bc'
  ]);

  /**
   * Colors from the dashboard Most Used Application chart (colors.BarColor),
   * excluding purple/theme colors that clash with timeline plots.
   */
  readonly taskBarColors: string[] = [ConductTimelogService.PRIMARY_CONDUCT_COLOR];

  private projectPaletteMap = new Map<string, ProjectColorPalette>();
  private projectColorMap = new Map<string, string>();
  private timelineCache = new Map<string, any[]>();
  private currentFetchKey = '';
  private lastLoadedKey = '';
  private timelineFetchSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private conductService: ConductTimelogService,
    private router: Router,
    @Optional() private dummyDataService?: DummyDataService,
    @Optional() private sharedService?: SharedService
  ) { }

  ngOnInit(): void {
    this.initDrawerSelectedDate();
    if (this.isOpen && this.group && !this.lastLoadedKey) {
      this.refreshDrawerData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen) {
      this.cancelPendingTimelineRequest();
      this.lastLoadedKey = '';
      return;
    }

    const isOpenChanged = !!changes['isOpen'] && changes['isOpen'].currentValue === true && !changes['isOpen'].previousValue;
    const groupChanged = !!changes['group'] && changes['group'].currentValue !== changes['group'].previousValue;

    let dateChanged = false;
    if (changes['selectedDate'] || changes['startDate'] || changes['endDate']) {
      dateChanged = true;
    }

    const sessionsChanged = !!changes['userTimelineSessions'] && changes['userTimelineSessions'].currentValue !== changes['userTimelineSessions'].previousValue;

    if (isOpenChanged || groupChanged || dateChanged || sessionsChanged) {
      if (isOpenChanged || dateChanged) {
        this.initDrawerSelectedDate();
      }
      if (groupChanged || dateChanged || sessionsChanged) {
        this.lastLoadedKey = '';
      }
      this.refreshDrawerData();
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingTimelineRequest();
    this.lastLoadedKey = '';
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cancelPendingTimelineRequest(): void {
    if (this.timelineFetchSub) {
      this.timelineFetchSub.unsubscribe();
      this.timelineFetchSub = undefined;
    }
    this.currentFetchKey = '';
    this.isLoadingDesktopTime = false;
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.isOpen) {
      this.closeDrawer();
    }
  }

  closeDrawer(): void {
    this.cancelPendingTimelineRequest();
    this.lastLoadedKey = '';
    this.close.emit();
    this.sharedService?.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'close',
      componentData: null,
      componentWidth: 0
    });
  }

  onPrevClick(): void {
    if (this.currentIndex > 0) {
      this.cancelPendingTimelineRequest();
      const newIndex = this.currentIndex - 1;
      this.navigateIndex.emit(newIndex);
      if (this.groups && this.groups[newIndex]) {
        this.currentIndex = newIndex;
        this.group = this.groups[newIndex];
        this.lastLoadedKey = '';
        this.initDrawerSelectedDate();
        this.refreshDrawerData();
      }
    }
  }

  onNextClick(): void {
    if (this.currentIndex < this.groups.length - 1) {
      this.cancelPendingTimelineRequest();
      const newIndex = this.currentIndex + 1;
      this.navigateIndex.emit(newIndex);
      if (this.groups && this.groups[newIndex]) {
        this.currentIndex = newIndex;
        this.group = this.groups[newIndex];
        this.lastLoadedKey = '';
        this.initDrawerSelectedDate();
        this.refreshDrawerData();
      }
    }
  }

  isDrawerPrevDateDisabled(): boolean {
    return moment(this.drawerSelectedDate).isSameOrBefore(moment(this.effectiveStartDate), 'day');
  }

  isDrawerNextDateDisabled(): boolean {
    return (
      moment(this.drawerSelectedDate).isSameOrAfter(moment(this.effectiveEndDate), 'day') ||
      moment(this.drawerSelectedDate).isSameOrAfter(moment().endOf('day'), 'day')
    );
  }

  onDrawerPrevDate(): void {
    if (this.isDrawerPrevDateDisabled()) {
      return;
    }
    this.cancelPendingTimelineRequest();
    this.drawerSelectedDate = moment(this.drawerSelectedDate).subtract(1, 'day').startOf('day').toDate();
    this.lastLoadedKey = '';
    this.rebuildVisibleDayCards();
    this.refreshDrawerData();
  }

  onDrawerNextDate(): void {
    if (this.isDrawerNextDateDisabled()) {
      return;
    }
    this.cancelPendingTimelineRequest();
    this.drawerSelectedDate = moment(this.drawerSelectedDate).add(1, 'day').startOf('day').toDate();
    this.lastLoadedKey = '';
    this.rebuildVisibleDayCards();
    this.refreshDrawerData();
  }

  onDrawerDatePicked(pickedDate: Date | null): void {
    if (!pickedDate) {
      return;
    }
    this.cancelPendingTimelineRequest();
    this.drawerSelectedDate = moment(pickedDate).startOf('day').toDate();
    this.lastLoadedKey = '';
    this.rebuildVisibleDayCards();
    this.refreshDrawerData();
  }

  private initDrawerSelectedDate(): void {
    const startM = moment(this.effectiveStartDate);
    const endM = moment(this.effectiveEndDate);

    if (this.isMultipleDates) {
      const currentM = moment(this.drawerSelectedDate);
      if (!currentM.isValid() || currentM.isBefore(startM, 'day') || currentM.isAfter(endM, 'day')) {
        this.drawerSelectedDate = startM.toDate();
      }
    } else {
      this.drawerSelectedDate = startM.toDate();
    }

    this.rebuildVisibleDayCards();
  }

  /**
   * Returns a consistent color for a given project from the dashboard application usage palette.
   * All task logs belonging to the same project share the exact same color.
   */
  getProjectColor(projectName?: string): string {
    return ConductTimelogService.PRIMARY_CONDUCT_COLOR;
  }

  /**
   * Returns the project-grouped color for a given task log.
   */
  getTaskLogColor(log?: ConductTimelog, index?: number): string {
    return ConductTimelogService.PRIMARY_CONDUCT_COLOR;
  }

  getProjectPalette(projectName?: string, index?: number): ProjectColorPalette {
    const key = (projectName || 'General Project').trim().toLowerCase();
    if (this.projectPaletteMap.has(key)) {
      return this.projectPaletteMap.get(key)!;
    }
    const color = this.getProjectColor(projectName);
    const palette: ProjectColorPalette = {
      tagBg: 'bg-slate-50',
      tagText: 'text-slate-800',
      tagBorder: 'border-slate-200',
      barHex: color
    };
    this.projectPaletteMap.set(key, palette);
    return palette;
  }

  formatTimeRange(log: ConductTimelog): string {
    const start = this.formatTime12h(log.from_time);
    const end = this.formatTime12h(log.end_time);
    if (start && end) {
      return `${start} – ${end}`;
    }
    return start || end || '—';
  }

  formatDurationBadge(log: ConductTimelog): string {
    const secs = this.getLogDurationSeconds(log);
    return this.formatDurationHhMm(secs);
  }

  /**
   * Resolves the target calendar date string (YYYY-MM-DD) for the current conduct logs.
   */
  getTargetDateStr(): string {
    if (this.isMultipleDates) {
      if (this.drawerSelectedDate) {
        const m = moment(this.drawerSelectedDate);
        if (m.isValid()) {
          return m.format('YYYY-MM-DD');
        }
      }
    }

    const d = this.startDate || this.selectedDate;
    if (d) {
      const m = moment(d);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD');
      }
    }

    if (this.drawerSelectedDate) {
      const m = moment(this.drawerSelectedDate);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD');
      }
    }

    if (this.group?.logs && this.group.logs.length > 0) {
      const firstLog = this.group.logs[0];
      const raw = firstLog.date || firstLog.log_date;
      if (raw) {
        const m = moment(raw);
        if (m.isValid()) {
          return m.format('YYYY-MM-DD');
        }
      }
    }
    return moment().format('YYYY-MM-DD');
  }

  /**
   * Verifies if a given timeline session belongs to targetDateStr.
   */
  private sessionMatchesDate(session: any, targetDateStr: string): boolean {
    if (!session) return false;
    const raw = session.date || session.inTime || session.in;
    if (!raw) return true;
    const rawStr = String(raw).trim();
    if (rawStr.includes('-') || rawStr.includes('/')) {
      const m = moment(rawStr);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD') === targetDateStr;
      }
    }
    return true;
  }

  exportEmployeeLogs(): void {
    if (!this.group || !this.group.logs || this.group.logs.length === 0) {
      return;
    }
    const dateStr = this.getTargetDateStr();
    const logsToExport = this.isMultipleDates && this.sortedLogs.length > 0 ? this.sortedLogs : this.group.logs;
    const exportData = logsToExport.map((log: ConductTimelog, idx: number) => ({
      '#': idx + 1,
      'Employee Name': this.group?.employee_name ?? '',
      'Employee Email': this.group?.employee_email ?? '',
      'Date': log.date ?? log.log_date ?? dateStr,
      'Project': log.project_name ?? 'General Project',
      'Task ID': log.task_unique_id ?? log.task_id ?? '',
      'Task Title': log.task_title ?? 'General Log',
      'Start Time': this.formatTime12h(log.from_time),
      'End Time': this.formatTime12h(log.end_time),
      'Duration': this.formatDurationHhMm(this.getLogDurationSeconds(log)),
      'Duration (Seconds)': this.getLogDurationSeconds(log)
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const safeName = (this.group.employee_name || 'employee')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_');
    saveAs(blob, `conduct_timelogs_${safeName}_${dateStr}.csv`);
  }

  private refreshDrawerData(): void {
    if (!this.isOpen || !this.group) {
      return;
    }

    if (!this.isMultipleDates) {
      this.drawerSelectedDate = this.effectiveStartDate;
    }

    this.rebuildVisibleDayCards();

    const currentKey = `${this.group?.user_key ?? this.group?.supersee_user_id}_${this.getTargetDateStr()}`;
    if (this.lastLoadedKey === currentKey && this.userTimelineSegments.length > 0) {
      return;
    }
    this.lastLoadedKey = currentKey;

    this.resolveEmployeeSubtitle();
    this.prepareSortedLogs();
    this.computeConductDuration();
    this.fetchOrComputeTimeline();
  }

  private resolveEmployeeSubtitle(): void {
    const targetDateStr = this.getTargetDateStr();
    this.formattedDateSubtitle = moment(targetDateStr).format('DD MMM YYYY');

    const empId = this.group?.supersee_user_id;
    let foundRole = '';

    if (empId && Array.isArray(this.users) && this.users.length > 0) {
      const matched = this.users.find(
        (u: any) => String(u.id ?? u.ID ?? u.userId) === String(empId)
      );
      if (matched) {
        if ((matched as any).designation) {
          foundRole = (matched as any).designation;
        } else if ((matched as any).job_title) {
          foundRole = (matched as any).job_title;
        } else if (matched.description) {
          foundRole = matched.description;
        } else if (Array.isArray(matched.role) && matched.role.length > 0) {
          const roleStr = matched.role[0];
          foundRole = roleStr.charAt(0).toUpperCase() + roleStr.slice(1);
        }
      }
    }

    this.userRoleSubtitle = foundRole || 'Team Member';
  }

  getActiveLogsForSelectedDate(): ConductTimelog[] {
    if (!this.group || !Array.isArray(this.group.logs)) {
      return [];
    }

    if (!this.isMultipleDates) {
      return this.group.logs;
    }

    const targetDateStr = this.getTargetDateStr();
    return this.group.logs.filter(log => {
      const logDate = this.extractLogDate(log);
      if (!logDate) {
        return targetDateStr === moment(this.effectiveStartDate).format('YYYY-MM-DD');
      }
      return logDate === targetDateStr;
    });
  }

  private extractLogDate(log: ConductTimelog): string {
    const raw = log.date || log.log_date;
    if (raw) {
      const m = moment(raw);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD');
      }
    }
    if (log.from_time && String(log.from_time).includes('T')) {
      const m = moment(log.from_time);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD');
      }
    }
    return '';
  }

  private prepareSortedLogs(): void {
    if (!this.group || !Array.isArray(this.group.logs)) {
      this.sortedLogs = [];
      this.projectColorMap.clear();
      return;
    }

    const activeLogs = this.getActiveLogsForSelectedDate();
    const rawSorted = [...activeLogs].sort((a, b) => {
      const minA = this.parseTimeToMinutes(a.from_time);
      const minB = this.parseTimeToMinutes(b.from_time);
      return minA - minB;
    });

    // Pre-build the color map so each project gets a stable color index.
    this.projectColorMap.clear();
    rawSorted.forEach((log) => {
      const key = (log.project_name || log.project_id || 'General Project').trim().toLowerCase();
      if (!this.projectColorMap.has(key)) {
        const color = this.taskBarColors[this.projectColorMap.size % this.taskBarColors.length];
        this.projectColorMap.set(key, color);
      }
    });

    // Enrich each log with its resolved color so the template never calls methods during CD.
    this.sortedLogs = rawSorted.map(log => ({
      ...log,
      resolvedColor: this.getProjectColor(log.project_name || log.project_id || 'General Project')
    }));
  }

  private computeConductDuration(): number {
    let totalSecs = 0;
    const activeLogs = this.getActiveLogsForSelectedDate();
    for (const log of activeLogs) {
      totalSecs += this.getLogDurationSeconds(log);
    }
    this.conductTimeFormatted = this.formatDurationHhMm(totalSecs);
    return totalSecs;
  }

  private fetchOrComputeTimeline(): void {
    const conductSecs = this.computeConductDuration();
    const userId = this.group?.supersee_user_id || this.group?.user_key;
    const dateStr = this.getTargetDateStr();

    // 1. If parent provided userTimelineSessions directly, ensure they belong to target dateStr
    if (Array.isArray(this.userTimelineSessions) && this.userTimelineSessions.length > 0) {
      const hasMatchingDate = this.userTimelineSessions.some((s: any) =>
        this.sessionMatchesDate(s, dateStr)
      );

      if (hasMatchingDate) {
        this.cancelPendingTimelineRequest();
        const validSessions = this.userTimelineSessions.filter((s: any) =>
          this.sessionMatchesDate(s, dateStr)
        );
        this.buildTimeline(validSessions, conductSecs);
        return;
      }
      // If provided sessions do NOT match the target conduct date, ignore them and fetch for dateStr!
    }

    // 2. If in demo data mode and dummyDataService has timeline data
    if (this.dummyDataService?.isDemoDataVisible) {
      try {
        const demoRes = this.dummyDataService.getDataByParams<any>('TimeLine')?.data;
        const userDemo = demoRes?.[String(userId)] || (userId ? Object.values(demoRes || {})[0] : null);
        const demoSessions = (userDemo as any)?.sessions || [];
        if (demoSessions.length > 0) {
          this.cancelPendingTimelineRequest();
          const validDemoSessions = demoSessions.filter((s: any) =>
            this.sessionMatchesDate(s, dateStr)
          );
          this.buildTimeline(validDemoSessions.length > 0 ? validDemoSessions : demoSessions, conductSecs);
          return;
        }
      } catch (e) {
        console.warn('[ConductTimelogsDrawer] Demo timeline lookup skipped', e);
      }
    }

    // 3. Check memory cache first to prevent redundant network requests
    const cacheKey = `${userId}_${dateStr}`;
    if (this.timelineCache.has(cacheKey)) {
      this.cancelPendingTimelineRequest();
      const cached = this.timelineCache.get(cacheKey) || [];
      this.buildTimeline(cached, conductSecs);
      return;
    }

    // 4. Guard against duplicate in-flight requests for the same user and date
    if (this.isLoadingDesktopTime && this.currentFetchKey === cacheKey) {
      return;
    }

    // Cancel any previous request for another user or date
    this.cancelPendingTimelineRequest();

    // 5. Otherwise fetch from backend API for the target conduct date
    if (userId) {
      this.isLoadingDesktopTime = true;
      this.currentFetchKey = cacheKey;

      this.timelineFetchSub = this.projectService
        .getUserApplicationLogs(String(userId), dateStr, dateStr)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.isLoadingDesktopTime = false;
            this.currentFetchKey = '';
            const dayKey = dateStr;
            const dayData = res?.[dayKey] ?? (Array.isArray(res) ? res[0] : res);
            const rawSessions = Array.isArray(dayData?.sessions)
              ? dayData.sessions
              : (Array.isArray(res?.sessions) ? res.sessions : []);

            const sessions = Array.isArray(rawSessions)
              ? rawSessions.filter((s: any) => this.sessionMatchesDate(s, dateStr))
              : [];

            this.timelineCache.set(cacheKey, sessions);
            this.buildTimeline(sessions, conductSecs);
            this.rebuildVisibleDayCards();
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            this.isLoadingDesktopTime = false;
            this.currentFetchKey = '';
            console.error('[ConductTimelogsDrawer] Failed to fetch user application logs for date:', dateStr, err);
            this.timelineCache.set(cacheKey, []);
            this.buildTimeline([], conductSecs);
            this.rebuildVisibleDayCards();
            this.cdr.markForCheck();
          }
        });
    } else {
      this.buildTimeline([], conductSecs);
    }
  }

  buildTimeline(desktopSessions: any[], conductSecs: number): void {
    let productiveSeconds = 0;
    let idleSeconds = 0;
    const userIntervals: {
      startMin: number;
      endMin: number;
      durationSecs: number;
      type: 'productive' | 'idle';
      appName?: string;
      windowName?: string;
    }[] = [];

    const targetDateStr = this.getTargetDateStr();

    // Process raw timeline sessions into rendered intervals with usage/idle state
    const processedSessions = Array.isArray(desktopSessions) && desktopSessions.length > 0
      ? processTimelineSessionLogs(desktopSessions)
      : [];

    processedSessions.forEach((sess: TimelineRenderedSession | any) => {
      // Safety filter: ensure session belongs to targetDateStr
      if (!this.sessionMatchesDate(sess, targetDateStr)) {
        return;
      }

      const inTime = sess.inTime ?? sess.in;
      const outTime = sess.outTime ?? sess.out;
      if (inTime && outTime) {
        const startMin = this.parseTimeToMinutes(inTime);
        const endMin = this.parseTimeToMinutes(outTime);
        if (endMin > startMin) {
          const isIdle =
            sess.label === 'idle' ||
            (sess.idle && sess.idle !== '00:00:00' && (!sess.usage || sess.usage === '00:00:00'));
          const durSecs = (endMin - startMin) * 60;

          if (isIdle) {
            idleSeconds += durSecs;
            userIntervals.push({
              startMin,
              endMin,
              durationSecs: durSecs,
              type: 'idle',
              appName: sess.appName,
              windowName: sess.windowName
            });
          } else {
            productiveSeconds += durSecs;
            userIntervals.push({
              startMin,
              endMin,
              durationSecs: durSecs,
              type: 'productive',
              appName: sess.appName,
              windowName: sess.windowName
            });
          }
        }
      }
    });

    const totalDesktopSeconds = productiveSeconds + idleSeconds;
    this.hasTimelineData = totalDesktopSeconds > 0;

    // Desktop System Time: productive + idle, or 00:00 if no data
    if (totalDesktopSeconds > 0) {
      this.desktopTimeFormatted = this.formatDurationHhMm(totalDesktopSeconds);
      this.desktopTimeSubtitle = `${this.formatDurationHhMm(productiveSeconds)} prod • ${this.formatDurationHhMm(idleSeconds)} idle`;
    } else {
      this.desktopTimeFormatted = '00:00';
      this.desktopTimeSubtitle = 'No timeline recorded';
    }

    const diffSeconds = Math.abs(totalDesktopSeconds - conductSecs);
    if (totalDesktopSeconds === 0 && conductSecs === 0) {
      this.timeVarianceFormatted = '00:00';
      this.matchPercentage = 0;
    } else if (totalDesktopSeconds === 0 || conductSecs === 0) {
      this.timeVarianceFormatted = this.formatVariance(diffSeconds);
      this.matchPercentage = 0;
    } else {
      this.timeVarianceFormatted = this.formatVariance(diffSeconds);
      this.matchPercentage = Math.min(
        100,
        Math.round((Math.min(totalDesktopSeconds, conductSecs) / Math.max(totalDesktopSeconds, conductSecs)) * 100)
      );
    }

    const taskIntervals: {
      log: ConductTimelog;
      startMin: number;
      endMin: number;
      durationSecs: number;
    }[] = [];

    this.sortedLogs.forEach((log: ConductTimelog) => {
      const durSecs = this.getLogDurationSeconds(log);
      let sMin = this.parseTimeToMinutes(log.from_time);
      let eMin = this.parseTimeToMinutes(log.end_time);

      if (sMin === 0 && eMin === 0 && durSecs > 0) {
        sMin = 9 * 60;
        eMin = sMin + Math.round(durSecs / 60);
      } else if (eMin <= sMin) {
        eMin = sMin + Math.max(15, Math.round(durSecs / 60));
      }

      taskIntervals.push({
        log,
        startMin: sMin,
        endMin: eMin,
        durationSecs: durSecs
      });
    });

    const unloggedGaps: { startMin: number; endMin: number; durationSecs: number }[] = [];
    if (totalDesktopSeconds > 0) {
      const desktopMinutes = new Uint8Array(1440);
      const conductMinutes = new Uint8Array(1440);

      userIntervals.forEach((ui) => {
        const s = Math.max(0, Math.min(1439, ui.startMin));
        const e = Math.max(0, Math.min(1440, ui.endMin));
        for (let m = s; m < e; m++) {
          desktopMinutes[m] = 1;
        }
      });

      taskIntervals.forEach((ti) => {
        const s = Math.max(0, Math.min(1439, ti.startMin));
        const e = Math.max(0, Math.min(1440, ti.endMin));
        for (let m = s; m < e; m++) {
          conductMinutes[m] = 1;
        }
      });

      let inGap = false;
      let gapStart = -1;
      for (let m = 0; m < 1440; m++) {
        const isGap = desktopMinutes[m] === 1 && conductMinutes[m] === 0;
        if (isGap) {
          if (!inGap) {
            inGap = true;
            gapStart = m;
          }
        } else {
          if (inGap) {
            inGap = false;
            const gapEnd = m;
            const dur = (gapEnd - gapStart) * 60;
            if (dur >= 60) {
              unloggedGaps.push({ startMin: gapStart, endMin: gapEnd, durationSecs: dur });
            }
            gapStart = -1;
          }
        }
      }
      if (inGap && gapStart !== -1) {
        const gapEnd = 1440;
        const dur = (gapEnd - gapStart) * 60;
        if (dur >= 60) {
          unloggedGaps.push({ startMin: gapStart, endMin: gapEnd, durationSecs: dur });
        }
      }
    }
    this.unloggedGapsCount = unloggedGaps.length;

    let totalUnloggedSeconds = 0;
    if (userIntervals.length > 0) {
      totalUnloggedSeconds = unloggedGaps.reduce((acc, g) => acc + g.durationSecs, 0);
    } else {
      totalUnloggedSeconds = Math.max(0, totalDesktopSeconds - conductSecs);
    }

    if (totalUnloggedSeconds > 0) {
      this.unloggedTimeFormatted = this.formatDurationHhMm(totalUnloggedSeconds);
    } else {
      this.unloggedTimeFormatted = '00h 00m';
    }

    let overallMin = 24 * 60;
    let overallMax = 0;

    userIntervals.forEach((i) => {
      if (i.startMin < overallMin) overallMin = i.startMin;
      if (i.endMin > overallMax) overallMax = i.endMin;
    });

    taskIntervals.forEach((i) => {
      if (i.startMin < overallMin) overallMin = i.startMin;
      if (i.endMin > overallMax) overallMax = i.endMin;
    });

    if (overallMax <= overallMin) {
      overallMin = 9 * 60; // 09:00 AM
      overallMax = 18 * 60; // 06:00 PM
    } else {
      const currentSpan = overallMax - overallMin;
      if (currentSpan < 6 * 60) {
        const pad = Math.round((6 * 60 - currentSpan) / 2);
        overallMin = Math.max(0, overallMin - pad);
        overallMax = Math.min(24 * 60, overallMax + pad);
      }
    }

    const totalSpanMinutes = Math.max(60, overallMax - overallMin);
    this.timelineRangeLabel = `${this.minutesTo12h(overallMin)} – ${this.minutesTo12h(overallMax)}`;

    // 1. Build User Timeline Segments (SuperSee Tracked row in #545de6)
    this.userTimelineSegments = [];
    userIntervals.sort((a, b) => a.startMin - b.startMin);

    userIntervals.forEach((interval) => {
      const left = ((interval.startMin - overallMin) / totalSpanMinutes) * 100;
      const width = Math.max(1.5, ((interval.endMin - interval.startMin) / totalSpanMinutes) * 100);
      const isIdle = interval.type === 'idle';
      const hexColor = isIdle ? '#f59e0b' : '#545de6';
      const typeLabel = isIdle ? 'Idle' : 'Tracked';
      const appInfo = interval.appName ? ` • ${interval.appName}` : '';

      this.userTimelineSegments.push({
        leftPercent: Math.max(0, Math.min(100, left)),
        widthPercent: Math.min(100 - left, width),
        label: typeLabel,
        tooltip: `${typeLabel}: ${this.minutesTo12h(interval.startMin)} – ${this.minutesTo12h(interval.endMin)} (${this.formatDurationDisplay(interval.durationSecs)})${appInfo}`,
        hexColor,
        isIdle
      });
    });
    this.systemUsageSegments = this.userTimelineSegments;

    // 2. Build Task Log Segments with SuperSee desktop active overlap
    this.taskLogSegments = [];
    taskIntervals.forEach((item, idx) => {
      const left = ((item.startMin - overallMin) / totalSpanMinutes) * 100;
      const width = Math.max(1.5, ((item.endMin - item.startMin) / totalSpanMinutes) * 100);
      const taskColor = this.getTaskLogColor(item.log, idx);
      const taskKey = item.log.task_unique_id ? `[${item.log.task_unique_id}] ` : '';
      const projName = item.log.project_name ? ` (${item.log.project_name})` : '';
      const durSecs = item.durationSecs > 0 ? item.durationSecs : (item.endMin - item.startMin) * 60;
      const durationText = this.formatDurationDisplay(durSecs);
      const timeRangeText = `${this.minutesTo12h(item.startMin)} – ${this.minutesTo12h(item.endMin)}`;

      const overlapDetails = this.conductService.calculateSuperSeeOverlapDetails(
        desktopSessions,
        item.startMin,
        item.endMin
      );
      const activeOverlapText = overlapDetails.activeText || this.formatDurationDisplay(overlapDetails.activeSecs);
      const diffSecs = Math.max(0, durSecs - overlapDetails.activeSecs);
      const differenceText = diffSecs > 0 ? this.formatDurationDisplay(diffSecs) : '';
      const idleSecs = overlapDetails.idleSecs || 0;
      const offlineSecs = overlapDetails.offlineSecs || 0;
      const idleText = overlapDetails.idleText || this.formatDurationDisplay(idleSecs);
      const offlineText = overlapDetails.offlineText || this.formatDurationDisplay(offlineSecs);

      this.taskLogSegments.push({
        leftPercent: Math.max(0, Math.min(100, left)),
        widthPercent: Math.min(100 - left, width),
        label: item.log.task_title || 'Task Log',
        tooltip: `${taskKey}${item.log.task_title || 'Task'}${projName}: ${timeRangeText} (${durationText})`,
        hexColor: taskColor,
        log: item.log,
        taskTitle: item.log.task_title || 'General Task',
        taskId: item.log.task_unique_id || item.log.task_id,
        projectName: item.log.project_name || 'General Project',
        startMin: item.startMin,
        endMin: item.endMin,
        timeRangeText,
        durationText,
        activeOverlapText,
        differenceText,
        differenceSecs: diffSecs,
        superSeeTimeRangeText: overlapDetails.superSeeTimeRangeText,
        superSeeDurationText: overlapDetails.superSeeDurationText,
        activeSecs: overlapDetails.activeSecs,
        idleSecs,
        offlineSecs,
        idleText,
        offlineText
      });
    });

    // 3. Build Unlogged Gap Segments (Unlogged row in red dashed border)
    this.unloggedSegments = [];
    unloggedGaps.forEach((gap) => {
      const left = ((gap.startMin - overallMin) / totalSpanMinutes) * 100;
      const width = Math.max(1.5, ((gap.endMin - gap.startMin) / totalSpanMinutes) * 100);
      const timeRange = `${this.minutesTo12h(gap.startMin)} – ${this.minutesTo12h(gap.endMin)}`;
      const durLabel = this.formatDurationDisplay(gap.durationSecs);

      this.unloggedSegments.push({
        leftPercent: Math.max(0, Math.min(100, left)),
        widthPercent: Math.min(100 - left, width),
        startMin: gap.startMin,
        endMin: gap.endMin,
        durationSecs: gap.durationSecs,
        timeRangeLabel: timeRange,
        durationLabel: durLabel,
        tooltip: `${timeRange} · ${durLabel} unlogged`
      });
    });

    // 4. Build Chronological Interleaved Feed (Tasks + Unlogged Banners)
    const feed: ConductTimelineFeedItem[] = [];

    this.sortedLogs.forEach((log) => {
      let sMin = this.parseTimeToMinutes(log.from_time);
      let eMin = this.parseTimeToMinutes(log.end_time);
      const durSecs = this.getLogDurationSeconds(log);
      if (sMin === 0 && eMin === 0 && durSecs > 0) {
        sMin = 9 * 60;
        eMin = sMin + Math.round(durSecs / 60);
      } else if (eMin <= sMin) {
        eMin = sMin + Math.max(15, Math.round(durSecs / 60));
      }

      feed.push({
        type: 'task',
        startMin: sMin,
        endMin: eMin,
        log,
        projectName: log.project_name || 'General Project',
        taskUniqueId: log.task_unique_id || log.task_id,
        taskTitle: log.task_title || 'General Task',
        timeRange: this.formatTimeRange(log),
        durationBadge: this.formatDurationDisplay(durSecs),
        projectPalette: this.getProjectPalette(log.project_name)
      });
    });

    unloggedGaps.forEach((gap) => {
      feed.push({
        type: 'unlogged',
        startMin: gap.startMin,
        endMin: gap.endMin,
        durationSecs: gap.durationSecs,
        timeRange: `${this.minutesTo12h(gap.startMin)} – ${this.minutesTo12h(gap.endMin)}`,
        durationLabel: this.formatDurationDisplay(gap.durationSecs),
        tooltip: `${this.minutesTo12h(gap.startMin)} – ${this.minutesTo12h(gap.endMin)} · ${this.formatDurationDisplay(gap.durationSecs)} tracked, not logged`
      });
    });

    feed.sort((a, b) => {
      if (a.startMin !== b.startMin) {
        return a.startMin - b.startMin;
      }
      return a.type === 'task' ? -1 : 1;
    });

    this.interleavedFeed = feed;

    this.buildTimelineTicks(overallMin, overallMax, totalSpanMinutes);
  }

  private buildTimelineTicks(startMin: number, endMin: number, spanMin: number): void {
    const tickCount = 6;
    const step = spanMin / (tickCount - 1);
    this.timelineTicks = [];

    for (let i = 0; i < tickCount; i++) {
      const min = Math.round(startMin + i * step);
      const left = ((min - startMin) / spanMin) * 100;
      const label = this.minutesTo12h(min);
      this.timelineTicks.push({
        label,
        leftPercent: Math.min(100, Math.max(0, left))
      });
    }
  }

  getLogDurationSeconds(log: ConductTimelog): number {
    if (log.duration_time_second !== undefined && log.duration_time_second !== null) {
      const parsed = parseInt(String(log.duration_time_second), 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    if (log.worked_hours && typeof log.worked_hours === 'string' && log.worked_hours.includes(':')) {
      const parts = log.worked_hours.split(':').map((p) => parseInt(p, 10) || 0);
      if (parts.length === 3) {
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
      }
      if (parts.length === 2) {
        return parts[0] * 3600 + parts[1] * 60;
      }
    }
    return 0;
  }

  formatDurationHhMm(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
  }

  formatDurationDisplay(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }

  formatShortDuration(seconds: number): string {
    const totalSecs = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    if (h > 0 && m > 0) {
      return `${h}h ${m}m`;
    }
    if (h > 0) {
      return `${h}h`;
    }
    if (m > 0) {
      return `${m}m`;
    }
    return '0h';
  }

  formatVariance(diffSeconds: number): string {
    const secs = Math.max(0, Math.floor(diffSeconds || 0));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) {
      return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
    }
    return `${m}m`;
  }

  parseTimeToMinutes(timeVal: unknown): number {
    if (!timeVal) return 0;
    const str = String(timeVal).trim();

    if (str.includes('T')) {
      const m = moment(str);
      if (m.isValid()) {
        return m.hours() * 60 + m.minutes();
      }
    }

    if (str.includes(' ') && (str.toUpperCase().includes('AM') || str.toUpperCase().includes('PM'))) {
      const m = moment(str, ['hh:mm:ss A', 'hh:mm A', 'h:mm A', 'h:mm:ss A']);
      if (m.isValid()) {
        return m.hours() * 60 + m.minutes();
      }
    }

    if (str.includes(' ')) {
      const spaceParts = str.split(' ');
      const timePart = spaceParts[1] || spaceParts[0];
      const colonParts = timePart.split(':');
      if (colonParts.length >= 2) {
        const h = parseInt(colonParts[0], 10) || 0;
        const m = parseInt(colonParts[1], 10) || 0;
        return h * 60 + m;
      }
    }

    const colonParts = str.split(':');
    if (colonParts.length >= 2) {
      const h = parseInt(colonParts[0], 10) || 0;
      const m = parseInt(colonParts[1], 10) || 0;
      return h * 60 + m;
    }

    return 0;
  }

  formatTime12h(timeVal: unknown): string {
    if (!timeVal) return '';
    const minutes = this.parseTimeToMinutes(timeVal);
    return this.minutesTo12h(minutes);
  }

  private minutesTo12h(minutes: number): string {
    const clamped = Math.max(0, Math.min(1439, Math.floor(minutes || 0)));
    let hours = Math.floor(clamped / 60);
    const mins = clamped % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${period}`;
  }

  trackByTimelineSegment(index: number, seg: TimelineSegment): string {
    return `${seg.label}_${seg.leftPercent}_${seg.widthPercent}`;
  }

  trackByTaskSegment(index: number, seg: TaskTimelineSegment): string | number {
    return seg.taskId ?? seg.taskTitle ?? index;
  }

  trackByTick(index: number, tick: { label: string; leftPercent: number }): string {
    return tick.label;
  }

  trackByUnloggedSegment(_index: number, seg: UnloggedTimelineSegment): string {
    return `${seg.startMin}_${seg.endMin}_${seg.durationSecs}`;
  }

  trackByFeedItem(index: number, item: ConductTimelineFeedItem): string | number {
    if (item.type === 'task') {
      return item.taskUniqueId ?? item.log?.id ?? `${item.startMin}_${item.taskTitle}`;
    }
    return `unlogged_${item.startMin}_${item.endMin}`;
  }

  trackByLog(index: number, log: ConductTimelog): string | number {
    return log.id ?? log.task_unique_id ?? log.task_id ?? index;
  }

  trackByDate(_index: number, card: DrawerDayCard): string {
    return card.dateStr;
  }

  private rebuildVisibleDayCards(): void {
    const all = this.allDaysInRange;
    if (all.length === 0) {
      this.visibleDayCards = [];
      this.cdr.markForCheck();
      return;
    }

    const windowSize = this.visibleCardWindowSize;
    const maxStart = Math.max(0, all.length - windowSize);
    let startIdx = Math.min(Math.max(0, this.cardWindowStartIndex), maxStart);

    const selectedMoment = moment(this.drawerSelectedDate);
    const selectedStr = selectedMoment.format('YYYY-MM-DD');
    const selectedIdx = all.findIndex(d => moment(d).isSame(selectedMoment, 'day'));

    if (selectedIdx !== -1) {
      if (selectedIdx < startIdx) {
        startIdx = selectedIdx;
      } else if (selectedIdx >= startIdx + windowSize) {
        startIdx = Math.min(selectedIdx - (windowSize - 1), maxStart);
      }
    }
    this.cardWindowStartIndex = startIdx;

    this.visibleDayCards = all.slice(startIdx, startIdx + windowSize).map(date => {
      const m = moment(date);
      const dateStr = m.format('YYYY-MM-DD');
      const health = this.getDayMatchHealth(dateStr);
      return {
        date,
        dateStr,
        dayOfWeek: m.format('ddd'),
        dayOfMonth: m.format('D'),
        fullLabel: m.format('ddd, DD MMM YYYY'),
        isSelected: dateStr === selectedStr,
        dotColorClass: health.colorClass,
        dotTooltip: health.tooltip
      };
    });
    this.cdr.markForCheck();
  }
}
